import { AloftArea, AloftPrice, isUndefined, orUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAllCostApproachExportData } from './commentarySelectors/selectAllCostApproachExportData.ts';

type AdditionalStructures = {
  costPerSqft?: AloftPrice;
  sqft?: AloftArea;
  totalCost: AloftPrice;
};

/**
 * The Cost Approach Export Data is not a complete cost approach based on the current shape
 * of ReportJSON. So this aggregates and calculates the final details to prepare the ReportJSON
 * subject cost approach.
 */
export const selectSubjectCostApproach = createSelector(
  [selectAllCostApproachExportData],
  (subjCostApproach) => {
    const additionalStructureArea = subjCostApproach.additionalStructureTotalArea;
    const additionalStructureCost = subjCostApproach.additionalStructureCostNew;

    let additionalStructures: AdditionalStructures | undefined;
    if (additionalStructureCost) {
      if (additionalStructureArea.gt(AloftArea.zero)) {
        additionalStructures = {
          costPerSqft: additionalStructureCost.div(additionalStructureArea.unitAmount),
          sqft: additionalStructureArea,
          totalCost: additionalStructureCost,
        };
      } else {
        additionalStructures = {
          totalCost: additionalStructureCost,
        };
      }
    }

    const totalBasementCost = subjCostApproach.basementTotalCost;
    const totalBasementArea = subjCostApproach.improvementsBasement;
    const basement =
      totalBasementCost && totalBasementArea?.unitAmount
        ? {
            costPerSqft: totalBasementCost.div(totalBasementArea.unitAmount),
            sqft: totalBasementArea,
            totalCost: totalBasementCost,
          }
        : undefined;

    const { costMultiplier } = subjCostApproach;
    const depreciated = toDepreciated(
      subjCostApproach.siteToImprovementRatio,
      subjCostApproach.depreciatedCostOfImprovements,
    );

    const depreciation = subjCostApproach.depreciation
      ? {
          physical: subjCostApproach.physicalDepreciation,
          functional: subjCostApproach.functionalDepreciation,
          external: subjCostApproach.externalDepreciation,
          total: subjCostApproach.depreciation,
        }
      : undefined;

    const gla = orUndefined((iGla: AloftArea) => AloftArea.fromNumber(iGla.unitAmount, iGla.unit))(
      subjCostApproach.improvementsGla,
    );

    const dwelling = toDwelling(subjCostApproach.dwellingCostNew, gla);

    const dwellingCostQuality = subjCostApproach.quality
      ? `Q${subjCostApproach.quality}`
      : undefined;

    const garageCarportSqft = AloftArea.zero.add(
      subjCostApproach.improvementsGarageCarport ?? AloftArea.zero,
    );
    const garageCarport =
      subjCostApproach.garageCost && garageCarportSqft.gt(AloftArea.zero)
        ? {
            costPerSqft: subjCostApproach.garageCost?.div(garageCarportSqft.unitAmount),
            sqft: garageCarportSqft,
            totalCost: subjCostApproach.garageCost,
          }
        : undefined;

    return {
      additionalStructures,
      basement,
      costMultiplier,
      depreciated,
      depreciation,
      dwelling,
      dwellingCostQuality,
      garageCarport,
      indicatedValue: subjCostApproach.subjectValue,
      new: toCostApproachLine(subjCostApproach.costNew),
      source: subjCostApproach.source,
      effectiveAge: subjCostApproach.effectiveAge,
      remainingEcoLife: subjCostApproach.remainingEconomicLife,
      site: subjCostApproach.opinionOfSiteValuePrice,
      sections: subjCostApproach.sections,
    };
  },
);

const toCostApproachLine = (
  cost?: AloftPrice,
  netAdjustment?: AloftPrice,
):
  | {
      cost: AloftPrice;
      netAdjustment: AloftPrice | undefined;
    }
  | undefined => {
  if (!cost) return undefined;

  return {
    cost: cost.roundToDollarLike(),
    netAdjustment,
  };
};

const toDepreciated = (improvementsToValueRatio?: number, cost?: AloftPrice) => {
  if (isUndefined(improvementsToValueRatio) || !cost) return undefined;

  return {
    cost: cost.roundToDollarLike(),
    netAdjustment: undefined,
    improvementsToValueRatio,
  };
};

const toDwelling = (totalCost: AloftPrice | undefined, sqft: AloftArea | undefined) => {
  if (!totalCost || !sqft || sqft.unitAmount === 0) return undefined;

  const costPerSqft = totalCost.div(sqft.unitAmount);

  return {
    totalCost: totalCost.roundToDollarLike(),
    sqft,
    costPerSqft,
  };
};
